import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import type { z } from 'zod';
import { PayloadApiError, useCreateCustomer, useCustomer, useUpdateCustomer } from '@/core/api';
import { useAuth } from '@/core/auth';
import { useIntl } from '@/core/i18n/use-intl';
import { UserManagement } from '@/features/shared/users';
import { PageHeader } from '@/shared/components/layout/page-header';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Separator,
} from '@/shared/components/ui';

import { customerFormSchema } from '../schema';
import type { CustomerFormValues } from '../types';

export default function ManageCustomer() {
  const { t } = useIntl();
  const { user: currentUser } = useAuth();
  const createCustomer = useCreateCustomer();
  const updateCustomer = useUpdateCustomer();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = !!id;

  const { data: customer } = useCustomer(id || '');
  const isGmnAdmin = currentUser?.role === 'gmn-admin';
  const [usage, setUsage] = useState({ userCount: 0, inviteCount: 0 });
  const [userQuotaInput, setUserQuotaInput] = useState('50');

  const form = useForm<z.input<typeof customerFormSchema>, any, CustomerFormValues>({
    resolver: zodResolver(customerFormSchema),
    defaultValues: {
      name: '',
      companyType: '',
      industry: '',
      website: '',
      userQuota: 50,
    },
  });
  const userQuotaValue = Number(form.watch('userQuota') ?? 0);
  const totalUsage = usage.userCount + usage.inviteCount;
  const quotaForUsage = isEditMode ? (customer?.userQuota ?? userQuotaValue) : userQuotaValue;
  const handleUsageChange = useCallback((nextUsage: typeof usage) => {
    setUsage(nextUsage);
  }, []);

  const lastSyncedCustomerRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isEditMode || !customer) {
      return;
    }

    const signature = `${customer.id}:${customer.updatedAt ?? ''}:${customer.userQuota ?? ''}`;
    if (lastSyncedCustomerRef.current === signature) {
      return;
    }

    const nextQuota = customer.userQuota || 50;
    form.reset({
      name: customer.name,
      companyType: customer.companyType || '',
      industry: customer.industry || '',
      website: customer.website || '',
      userQuota: nextQuota,
    });
    setUserQuotaInput(String(nextQuota));
    lastSyncedCustomerRef.current = signature;
  }, [isEditMode, customer, form]);

  useEffect(() => {
    if (isEditMode) return;
    const currentValue = form.getValues('userQuota');
    if (typeof currentValue === 'number' && !Number.isNaN(currentValue)) {
      setUserQuotaInput(String(currentValue));
    } else {
      setUserQuotaInput('');
    }
  }, [isEditMode, form]);

  const handleQuotaValidationErrors = (error: unknown) => {
    if (!(error instanceof PayloadApiError)) return false;
    const messages =
      error.response?.errors?.map((err) => {
        if (typeof err === 'object' && err && 'message' in err) {
          return String((err as { message: string }).message);
        }
        return '';
      }) ?? [];

    if (messages.includes('CustomerQuotaBelowMinimum')) {
      form.setError('userQuota', {
        type: 'manual',
        message: t('errors.quotaBelowMinimum'),
      });
      return true;
    }

    if (messages.includes('CustomerQuotaBelowActiveUsers')) {
      form.setError('userQuota', {
        type: 'manual',
        message: t('errors.quotaBelowActiveUsers'),
      });
      return true;
    }

    return false;
  };

  const handleSubmit = async (data: CustomerFormValues) => {
    if (typeof data.userQuota !== 'number' || Number.isNaN(data.userQuota) || data.userQuota < 5) {
      form.setError('userQuota', {
        type: 'manual',
        message: t('errors.quotaBelowMinimum'),
      });
      return;
    }

    if (!isGmnAdmin) return;

    try {
      if (isEditMode) {
        await updateCustomer.mutateAsync({ id, data });
        toast.success(t('customers.customerUpdatedSuccessfully'));
      } else {
        const createdCustomer = await createCustomer.mutateAsync(data);
        toast.success(t('customers.customerCreatedSuccessfully'));

        navigate(`/gmn/customer-management/customers/${createdCustomer.doc.id}`);
      }
    } catch (error) {
      const handled = handleQuotaValidationErrors(error);
      if (!handled) {
        toast.error(t('customers.errorSavingCustomer'));
      }
    }
  };

  return (
    <>
      <PageHeader
        title={isEditMode ? t('customers.editCustomer') : t('customers.newCustomer')}
        backHref="/gmn/customer-management/customers"
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => navigate('/gmn/customer-management/customers')}
            >
              {t('common.discard')}
            </Button>
            <Button
              onClick={form.handleSubmit(handleSubmit)}
              disabled={
                (isEditMode ? updateCustomer.isPending : createCustomer.isPending) || !isGmnAdmin
              }
            >
              {isEditMode
                ? updateCustomer.isPending
                  ? t('common.updating')
                  : t('common.update')
                : createCustomer.isPending
                  ? t('common.creating')
                  : t('common.create')}
            </Button>
          </div>
        }
      />

      <div className="grid gap-6">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="grid gap-6">
            <div className="grid gap-6 lg:grid-cols-12">
              <Card className="lg:col-span-8 xl:col-span-9">
                <CardHeader>
                  <CardTitle>{t('customers.customerDetails')}</CardTitle>
                  <CardDescription>{t('customers.customerDetailsDescription')}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField
                      control={form.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel required>{t('common.name')}</FormLabel>
                          <FormControl>
                            <Input {...field} disabled={!isGmnAdmin} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="companyType"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('customers.companyType')}</FormLabel>
                          <FormControl>
                            <Input {...field} disabled={!isGmnAdmin} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="industry"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('customers.industry')}</FormLabel>
                          <FormControl>
                            <Input {...field} disabled={!isGmnAdmin} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="website"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('customers.website')}</FormLabel>
                          <FormControl>
                            <Input {...field} disabled={!isGmnAdmin} type="url" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </CardContent>
              </Card>

              <Card className="h-fit lg:col-span-4 xl:col-span-3">
                <CardHeader>
                  <CardTitle>{t('customers.userQuotaLabel')}</CardTitle>
                  <CardDescription>{t('customers.userQuotaDescription')}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <FormField
                    control={form.control}
                    name="userQuota"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel required>{t('customers.userQuotaLabel')}</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            type="number"
                            disabled={!isGmnAdmin}
                            value={userQuotaInput}
                            onChange={(event) => {
                              const nextValue = event.target.value;
                              setUserQuotaInput(nextValue);
                              if (nextValue === '') {
                                field.onChange(undefined);
                                return;
                              }
                              const parsed = Number(nextValue);
                              field.onChange(Number.isNaN(parsed) ? undefined : parsed);
                            }}
                            onBlur={(event) => {
                              const value = event.target.value;
                              setUserQuotaInput(value);
                              if (value === '') {
                                field.onChange(undefined);
                                form.setError('userQuota', {
                                  type: 'manual',
                                  message: t('errors.quotaBelowMinimum'),
                                });
                                return;
                              }
                              const parsedValue = Number(value);
                              if (Number.isNaN(parsedValue)) {
                                field.onChange(undefined);
                                form.setError('userQuota', {
                                  type: 'manual',
                                  message: t('errors.quotaBelowMinimum'),
                                });
                                return;
                              }
                              field.onChange(parsedValue);
                              if (parsedValue < 5) {
                                form.setError('userQuota', {
                                  type: 'manual',
                                  message: t('errors.quotaBelowMinimum'),
                                });
                              } else {
                                form.clearErrors('userQuota');
                              }
                            }}
                          />
                        </FormControl>
                        <p className="text-sm text-muted-foreground">
                          {t('customers.userQuotaHelper')}
                        </p>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="space-y-1">
                    <p className="text-sm font-medium">
                      {t('customers.userQuotaUsage', {
                        used: totalUsage,
                        quota: quotaForUsage,
                      })}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {t('customers.userQuotaUsageDetails', {
                        userCount: usage.userCount,
                        inviteCount: usage.inviteCount,
                      })}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </form>
        </Form>

        {isEditMode && (
          <>
            <Separator />
            <UserManagement
              allowedRoles={['customer-admin']}
              customerId={id}
              hideInviteButton={!isGmnAdmin}
              title={t('users.users')}
              onUsageChange={handleUsageChange}
            />
          </>
        )}
      </div>
    </>
  );
}
